import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { User } from "./interfaces/User";
import RoleBadge from "./RoleBadge";
import { MdContentCopy, MdPerson } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

interface UserCardProps {
  user: User;
  onClick?: () => void;
}

export default function UserCard({ user, onClick }: UserCardProps) {
  const navigate = useNavigate();
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/users/${user.uid}`, { state: { user } });
    }
  };

  const handleCopyUserId = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when copying
    try {
      await navigator.clipboard.writeText(user.uid);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy user ID:', error);
    }
  };

  const getDisplayName = () => {
    if (user.display_name) {
      return user.display_name;
    }
    // If user.email is not of type string, return "-"
    if (typeof user.email !== 'string') {
      return "-";
    }
    // Extract name from email if display_name is not available
    const emailParts = user.email.split('@');
    return emailParts[0];
  };

  return (
    <Card
      className="h-100 user-card"
      style={{
        cursor: 'pointer',
        backgroundColor: '#1a1d1f',
        borderColor: '#495057',
        transition: 'transform 0.2s, box-shadow 0.2s'
      }}
      onClick={handleCardClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <Card.Body className="p-3">
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle bg-secondary d-flex align-items-center justify-content-center me-3"
              style={{ width: '40px', height: '40px' }}
            >
              {user.profile_url ? (
                <img
                  src={user.profile_url}
                  alt="Profile"
                  className="rounded-circle"
                  style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                />
              ) : (
                <MdPerson color="white" size={20} />
              )}
            </div>
            <div className="flex-grow-1">
              <Card.Title className="mb-1 text-white" style={{ fontSize: '1rem' }}>
                {getDisplayName()}
              </Card.Title>
              <Card.Text className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                {user.email}
              </Card.Text>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between mt-3">
          <div className="d-flex align-items-center">
            <span className="text-muted me-2" style={{ fontSize: '0.75rem' }}>
              Role:
            </span>
            <RoleBadge role={user.role} planId={user.plan_id} />
          </div>

          <OverlayTrigger
            overlay={
              <Tooltip id={`copy-${user.uid}`}>
                {copySuccess ? 'Copied!' : 'Copy User ID'}
              </Tooltip>
            }
          >
            <div
              onClick={handleCopyUserId}
              style={{ cursor: 'pointer' }}
              className="text-light"
            >
              <MdContentCopy
                size={16}
                color={copySuccess ? '#28a745' : '#6c757d'}
              />
            </div>
          </OverlayTrigger>
        </div>
      </Card.Body>
    </Card>
  );
}